import React from "react";
import { useI18n } from "@/lib/i18n";

// Vibrant, distinct color per material category against pitch-black background.
// Accepts both English (backend catalog) and Turkish category names so the
// diagram paints correctly regardless of the source labelling.
function colorFor(category) {
  const k = (category || "").toLowerCase();
  if (k === "yalıtım"   || k === "insulation")            return "#FFB300"; // amber/orange
  if (k === "duvar"     || k === "wall"      || k === "masonry")     return "#C2410C"; // brick red
  if (k === "yapısal"   || k === "structural")            return "#6B7280"; // gray (concrete)
  if (k === "sıva"      || k === "plaster"   || k === "render")      return "#E8DCC4"; // beige
  if (k === "membran"   || k === "membrane")              return "#3B82F6"; // blue
  if (k === "kaplama"   || k === "cladding"  || k === "finish")      return "#8B5E3C"; // warm brown
  return "#A1A1AA";
}

function patternFor(category) {
  const k = (category || "").toLowerCase();
  if (k === "yalıtım"   || k === "insulation")            return "hatch-insulation";
  if (k === "duvar"     || k === "wall"      || k === "masonry")     return "hatch-masonry";
  if (k === "yapısal"   || k === "structural")            return "hatch-concrete";
  if (k === "sıva"      || k === "plaster"   || k === "render")      return "hatch-render";
  if (k === "membran"   || k === "membrane")              return "hatch-membrane";
  if (k === "kaplama"   || k === "cladding"  || k === "finish")      return "hatch-cladding";
  return "hatch-generic";
}

export default function WallSectionDrawing({ layers = [], materials = [], conditions }) {
  const { t } = useI18n();
  const W = 360;
  const H = 380;
  const padX = 56;
  const padY = 56;
  const drawW = W - padX * 2;
  const drawH = H - padY * 2;

  const totalThickness = layers.reduce((s, l) => s + (Number(l.thickness) || 0), 0);

  let cursorY = padY;
  const bands = layers.map((l, i) => {
    const h = totalThickness > 0 ? (Number(l.thickness) / totalThickness) * drawH : 0;
    const mat = materials.find((m) => m.name === l.name);
    const category = mat?.category || "Genel";
    const band = {
      l, i, h, y: cursorY,
      category,
      brand: mat?.brand || "",
      fill: colorFor(category),
      pattern: patternFor(category),
    };
    cursorY += h;
    return band;
  });

  const indoorT = conditions?.indoor_temp ?? 20;
  const outdoorT = conditions?.outdoor_temp ?? 0;
  const isLight = conditions?._mode === "day";

  // OUTSIDE-the-diagram palette (page-level labels above/below the SVG).
  // Light mode → solid black, no glow. Dark mode → solid white, no glow.
  // The INSIDE-the-diagram annotations (on the dark SVG canvas) keep their
  // bright neon + drop-shadow look regardless of theme.
  const outerTextColor = isLight ? "#000000" : "#FFFFFF";
  const outerMutedColor = isLight ? "#475569" : "rgba(255,255,255,0.7)";
  // Outer wrapper = bold blue "smart module" frame in both modes.
  const wrapperBorder = isLight ? "#1E40AF" : "#0070F3";
  const wrapperBg     = isLight ? "#FFFFFF" : "#000000";

  return (
    <div
      className="p-5"
      style={{
        backgroundColor: wrapperBg,
        border: `3px solid ${wrapperBorder}`,
        borderRadius: "10px",
        boxShadow: isLight
          ? "0 0 6px rgba(30,64,175,0.25), 0 4px 14px rgba(30,64,175,0.10)"
          : "0 0 8px rgba(0,112,243,0.45), 0 0 18px rgba(0,112,243,0.18)",
      }}
      data-testid="wall-section-drawing">
      <div className="flex items-center justify-between mb-2">
        <h3
          className="font-mono text-sm font-extrabold tracking-[0.22em] uppercase"
          style={{
            color: outerTextColor,
            textShadow: "none",
          }}>
          ◆ {t("draw.title", "Technical Section")}
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-wider font-bold"
          style={{ color: outerMutedColor }}>
          {t("draw.scale", "SCALE 1:2")}
        </span>
      </div>

      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{
          background: "#0A0A0A",
          border: `3px solid ${isLight ? "#1E40AF" : "#0070F3"}`,
          borderRadius: "6px",
          boxShadow: isLight
            ? "0 0 8px rgba(30,64,175,0.35), 0 0 16px rgba(30,64,175,0.15)"
            : "0 0 10px rgba(0,112,243,0.55), 0 0 22px rgba(0,112,243,0.25)",
        }}>
        <defs>
          <pattern id="hatch-insulation" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#000" strokeOpacity="0.45" strokeWidth="1"/>
          </pattern>
          <pattern id="hatch-concrete" patternUnits="userSpaceOnUse" width="8" height="8">
            <circle cx="2" cy="2" r="0.9" fill="#000" fillOpacity="0.5"/>
            <circle cx="6" cy="5" r="0.7" fill="#000" fillOpacity="0.4"/>
          </pattern>
          <pattern id="hatch-masonry" patternUnits="userSpaceOnUse" width="22" height="11">
            <rect width="22" height="11" fill="none"/>
            <line x1="0" y1="0" x2="22" y2="0" stroke="#000" strokeOpacity="0.55" strokeWidth="0.9"/>
            <line x1="11" y1="0" x2="11" y2="11" stroke="#000" strokeOpacity="0.55" strokeWidth="0.9"/>
            <line x1="0" y1="5.5" x2="22" y2="5.5" stroke="#000" strokeOpacity="0.55" strokeWidth="0.9"/>
            <line x1="0" y1="5.5" x2="0" y2="11" stroke="#000" strokeOpacity="0.55" strokeWidth="0.9"/>
          </pattern>
          <pattern id="hatch-render" patternUnits="userSpaceOnUse" width="3" height="3">
            <line x1="0" y1="0" x2="3" y2="3" stroke="#000" strokeOpacity="0.4" strokeWidth="0.4"/>
          </pattern>
          <pattern id="hatch-membrane" patternUnits="userSpaceOnUse" width="4" height="4">
            <line x1="0" y1="2" x2="4" y2="2" stroke="#fff" strokeOpacity="0.85" strokeWidth="0.8"/>
          </pattern>
          <pattern id="hatch-cladding" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(-45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#000" strokeOpacity="0.4" strokeWidth="0.6"/>
          </pattern>
          <pattern id="hatch-generic" patternUnits="userSpaceOnUse" width="4" height="4">
            <line x1="0" y1="0" x2="4" y2="4" stroke="#fff" strokeOpacity="0.3" strokeWidth="0.4"/>
          </pattern>
        </defs>

        {/* Side temperature labels */}
        <text x={padX - 8} y={padY - 18} textAnchor="end"
          fontFamily="ui-monospace, monospace" fontSize="11" fontWeight="800" fill="#FF3B30"
          style={{ filter: "drop-shadow(0 0 4px #FF3B30CC) drop-shadow(0 0 8px #FF3B3066)" }}>
          İÇ {indoorT}°C
        </text>
        <text x={padX + drawW + 8} y={padY - 18} textAnchor="start"
          fontFamily="ui-monospace, monospace" fontSize="11" fontWeight="800" fill="#60A5FA"
          style={{ filter: "drop-shadow(0 0 4px #3B82F6CC) drop-shadow(0 0 8px #3B82F666)" }}>
          DIŞ {outdoorT}°C
        </text>

        {/* Heat flow arrow (with cyan/red glow for emphasis) */}
        <line x1={padX - 30} y1={padY + drawH / 2} x2={padX - 6} y2={padY + drawH / 2}
          stroke="#FF3B30" strokeWidth="2.2"
          style={{ filter: "drop-shadow(0 0 3px #FF3B30AA)" }}/>
        <polygon
          points={`${padX-6},${padY+drawH/2} ${padX-14},${padY+drawH/2-5} ${padX-14},${padY+drawH/2+5}`}
          fill="#FF3B30"
          style={{ filter: "drop-shadow(0 0 3px #FF3B30AA)" }}/>

        {/* Outer wall frame */}
        <rect x={padX} y={padY} width={drawW} height={drawH}
          fill="none" stroke="#00E5FF" strokeWidth="1.8" strokeOpacity="0.95"
          style={{ filter: "drop-shadow(0 0 3px #00E5FF88)" }}/>

        {/* Empty state */}
        {bands.length === 0 && (
          <text x={W / 2} y={H / 2} textAnchor="middle"
            fontFamily="ui-monospace, monospace" fontSize="11" fontWeight="700" fill="#FFFFFF"
            style={{ filter: "drop-shadow(0 0 4px #00E5FF99)" }}>
            Add layers in the Calculation tab
          </text>
        )}

        {/* Layer bands */}
        {bands.map((b, i) => (
          <g key={i}>
            <rect x={padX} y={b.y} width={drawW} height={b.h} fill={b.fill}/>
            <rect x={padX} y={b.y} width={drawW} height={b.h} fill={`url(#${b.pattern})`}/>
            <line x1={padX} y1={b.y + b.h} x2={padX + drawW} y2={b.y + b.h}
              stroke="#000" strokeOpacity="0.6" strokeWidth="0.5"/>

            {/* Dimension on left — bright cyan, neon-edged */}
            <text x={padX - 6} y={b.y + b.h / 2 + 3} textAnchor="end"
              fontFamily="ui-monospace, monospace" fontSize="10" fontWeight="800" fill="#00FFFF"
              style={{ filter: "drop-shadow(0 0 3px #00E5FFCC) drop-shadow(0 0 6px #00E5FF66)" }}>
              {(b.l.thickness * 100).toFixed(1)} cm
            </text>
            {/* Label on right — pure white with subtle cyan halo */}
            <text x={padX + drawW + 6} y={b.y + b.h / 2 - 1}
              fontFamily="ui-monospace, monospace" fontSize="10" fontWeight="800" fill="#FFFFFF"
              style={{ filter: "drop-shadow(0 0 3px #FFFFFFAA) drop-shadow(0 0 6px #00E5FF55)" }}>
              L{i + 1} · {b.l.name.length > 22 ? b.l.name.slice(0, 22) + "…" : b.l.name}
            </text>
            {/* λ/μ sub-label — switched from dark gray to bright lime so it stays legible */}
            <text x={padX + drawW + 6} y={b.y + b.h / 2 + 10}
              fontFamily="ui-monospace, monospace" fontSize="8.5" fontWeight="700" fill="#B6FF00"
              style={{ filter: "drop-shadow(0 0 2px #B6FF0099)" }}>
              λ={b.l.lambda_value} · μ={b.l.mu ?? 1}
            </text>
          </g>
        ))}

        {/* Total dimension on bottom */}
        <line x1={padX} y1={padY + drawH + 18} x2={padX + drawW} y2={padY + drawH + 18}
          stroke="#00E5FF" strokeWidth="1.1" strokeOpacity="0.95"
          style={{ filter: "drop-shadow(0 0 3px #00E5FF88)" }}/>
        <line x1={padX} y1={padY + drawH + 14} x2={padX} y2={padY + drawH + 22}
          stroke="#00E5FF" strokeWidth="1.1" strokeOpacity="0.95"
          style={{ filter: "drop-shadow(0 0 3px #00E5FF88)" }}/>
        <line x1={padX + drawW} y1={padY + drawH + 14} x2={padX + drawW} y2={padY + drawH + 22}
          stroke="#00E5FF" strokeWidth="1.1" strokeOpacity="0.95"
          style={{ filter: "drop-shadow(0 0 3px #00E5FF88)" }}/>
        <text x={padX + drawW / 2} y={padY + drawH + 34} textAnchor="middle"
          fontFamily="ui-monospace, monospace" fontSize="11" fontWeight="900" fill="#00FFFF"
          style={{ filter: "drop-shadow(0 0 4px #00E5FFCC) drop-shadow(0 0 8px #00E5FF66)" }}>
          {t("draw.toplam", "TOTAL")}: {(totalThickness * 100).toFixed(1)} cm
        </text>
      </svg>

      {/* Legend — OUTSIDE the diagram, normal theme-aware text (no glow) */}
      <div className="mt-3 grid grid-cols-3 gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider">
        {[
          ["Yalıtım", t("draw.legend.insulation", "Insulation")],
          ["Duvar",   t("draw.legend.wall", "Wall")],
          ["Yapısal", t("draw.legend.structural", "Structural")],
          ["Sıva",    t("draw.legend.plaster", "Plaster")],
          ["Membran", t("draw.legend.membrane", "Membrane")],
          ["Kaplama", t("draw.legend.cladding", "Cladding")],
        ].map(([key, label]) => (
          <div key={key} className="flex items-center gap-1.5"
            style={{ color: outerTextColor, textShadow: "none" }}>
            <span
              className="inline-block w-3 h-3 border"
              style={{
                background: colorFor(key),
                borderColor: isLight ? "#94A3B8" : "#FFFFFF55",
              }}/>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
